import { Close } from "@carbon/icons-react";
import cx from "classnames";
import type { FC, HTMLAttributes, ReactNode } from "react";

import { LEVEL_VARIANT_CLASSES } from "./constants";
import { type ChipLevel, type ColorVariant } from "./types";

export * from "./utils";

export type ChipProps = {
	colorVariant?: ColorVariant;
	fontVariant?: "default" | "code" | "no-caps";
	level?: ChipLevel;
	isDismissible?: boolean;
	onDismiss?: (e: any) => any;
	size?: "sm" | "md" | "lg";
	children: ReactNode;
	inline?: boolean;
} & HTMLAttributes<HTMLSpanElement>;

const FONT_CLASSES = {
	"default": "uppercase tracking-wider text-xxs font-bold",
	"code": "font-mono text-xs font-medium",
	"no-caps": "text-xs font-medium",
};

export const Chip: FC<ChipProps> = ({
	colorVariant = "default",
	fontVariant = "default",
	level = "secondary",
	isDismissible = false,
	onDismiss,
	size = "md",
	children,
	className = "",
	inline = true,
	...props
}) => {
	const bgClasses = LEVEL_VARIANT_CLASSES[level][colorVariant]["background"];
	const textColorClasses = LEVEL_VARIANT_CLASSES[level][colorVariant]["text"];
	const dismissButtonClasses = cx(
		LEVEL_VARIANT_CLASSES[level][colorVariant]["hover"],
		LEVEL_VARIANT_CLASSES[level][colorVariant]["groupHover"],
		LEVEL_VARIANT_CLASSES[level][colorVariant]["active"],
		LEVEL_VARIANT_CLASSES[level][colorVariant]["groupActive"]
	);
	const iconClasses = cx(
		LEVEL_VARIANT_CLASSES[level][colorVariant]["icon"],
		LEVEL_VARIANT_CLASSES[level][colorVariant]["iconHover"],
		LEVEL_VARIANT_CLASSES[level][colorVariant]["iconActive"]
	);

	const css = cx(
		"rounded",
		inline ? "inline-flex" : "flex",
		FONT_CLASSES[fontVariant],
		!className.includes("bg") && bgClasses,
		textColorClasses,
		className
	);

	const Dismissible = isDismissible
		? () => {
				return (
					<button
						onClick={(e) => onDismiss?.(e)}
						tabIndex={-1}
						aria-label={`Dismiss Chip`}
						className={cx(
							// Remove weird * > transition-all flashing on Firefox.
							"transition-none",
							"rounded-r",
							"group",
							dismissButtonClasses,
							["hover:outline-none", "focus:outline-none"],
							size === "sm" && ["p-0"],
							size === "md" && ["py-0", "px-1"],
							size === "lg" && ["py-0", "px-1.5"]
						)}
					>
						<Close size={16} className={iconClasses} />
					</button>
				);
			}
		: () => null;

	return (
		<span className={css} {...props}>
			<span
				className={cx(
					isDismissible && "rounded-r-none",
					size === "sm" && !isDismissible && ["rounded-sm", "px-1.5", "py-0"],
					size === "sm" && isDismissible && ["rounded-sm", "pl-1.5", "pr-1", "py-0"],
					size === "md" && !isDismissible && ["rounded", "px-2.5", "py-1"],
					size === "md" && isDismissible && ["rounded", "pl-2.5", "pr-1", "py-1"],
					size === "lg" && !isDismissible && ["rounded-sm", "px-3", "py-2"],
					size === "lg" && isDismissible && ["rounded-sm", "pl-3", "pr-1.5", "py-2"]
				)}
			>
				{children}
			</span>
			<Dismissible />
		</span>
	);
};
